import AdminHeader from "components/Headers/AdminHeader";
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Badge,
  Container,
  Row,
  Col,
  Card,
  Input,
  CardHeader,
  CardBody,
  UncontrolledTooltip,
  UncontrolledPopover,
  PopoverBody,
  CardTitle,
} from "reactstrap";
import Select, { components } from "react-select";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import {
  fixedWidthCustomStyle,
  DEFAULT_RECORDS_PER_PAGE,
} from "constants/CommonConstants";
import { STATUS_OPTIONS } from "components/constant";
import { urlEncodedId } from "components/CommonComponents/encodeDecodeFunction";
import { useDispatch, useSelector } from "react-redux";
import { getInstructorList, editInstructor } from "action/instructor";
import { instructorList } from "constants/instructor";
import {
  showAlert,
  toTitleCase,
} from "components/CommonComponents/CommonFunctions";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";
import { REGEX } from "constants/CommonConstants";

/* COMPONENT  */
const Instructors = () => {
  const authData = JSON.parse(localStorage.getItem("p") || "{}");
  const role = authData?.role;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { notify } = useContext(NotificationContext);
  const instructorListData = useSelector(
    (state) => state.instructor.instructorList,
  );
  const [instructorListState, setInstructorListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });
  const [pageNum, setPageNum] = useState(1);
  const [searchState, setSearchState] = useState({
    searchName: "",
    searchEmail: "",
    searchMobile: "",
    searchStatus: null,
    filterParams: [],
  });
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const formattedInstructorList = instructorListData?.data?.trainers?.map(
    (item, index) => {
      return {
        ...item,
        // ID: item?.ID,
        srNo:
          (pageNum - 1) *
            (instructorListData?.data?.noOfRecordsPerPage ||
              DEFAULT_RECORDS_PER_PAGE) +
          index +
          1,
        fullName: `${item?.user?.firstName || ""} ${
          item?.user?.lastName || ""
        }`,
        email: item?.user?.email || "",
        mobile: item?.user?.mobile || "",
        gender: toTitleCase(item?.gender) || "-",
        skills: item?.skills?.length
          ? item.skills.map((skill) => skill.name)
          : [],
        totalExperience: item?.totalExperience || "-",
        status: item?.user?.status || "-",
        createdOn: item?.user?.createdOn || "-",
        createdBy: item?.user?.createdBy || "—",
      };
    },
  );

  //get the list of instructor
  useEffect(() => {
    getInstructorList(dispatch, pageNum, searchState.filterParams);
    return () => {
      dispatch({ type: instructorList, payload: {} }); //to clear the technician redux state on unmount
    };
  }, [dispatch, pageNum, searchState.filterParams]);

  //to check whether data get loaded or not using code or message
  useEffect(() => {
    if (instructorListData?.code === 200) {
      setInstructorListState({
        isLoading: false,
        error: "",
        noRecords: instructorListData?.data?.trainers?.length === 0,
        showLoaderOnClick: false,
      });
    } else if (instructorListData?.code || instructorListData?.message) {
      setInstructorListState({
        isLoading: false,
        error:
          instructorListData?.message ||
          "Failed to load instructor list! Please try again.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [instructorListData]);

  // filters for search
  const buildFilterParams = () => {
    const filterParams = [];

    if (searchState.searchName?.trim()) {
      filterParams.push({
        field: "FullName",
        condition: "match",
        filterValues: [searchState.searchName.trim()],
      });
    }

    if (searchState.searchEmail?.trim()) {
      filterParams.push({
        field: "Email",
        condition: "like",
        filterValues: [searchState.searchEmail.trim()],
      });
    }

    if (searchState.searchMobile?.trim()) {
      filterParams.push({
        field: "Mobile",
        condition: "like",
        filterValues: [searchState.searchMobile.trim()],
      });
    }

    if (searchState.searchStatus) {
      filterParams.push({
        field: "Status",
        condition: "eq",
        filterValues: [searchState.searchStatus.value],
      });
    }
    return filterParams.length > 0 ? JSON.stringify(filterParams) : [];
  };

  const columns = [
    {
      Header: "Sr. No.",
      accessor: "srNo",
      minWidth: 60,
      maxWidth: 70,
      centerAlign: true,
    },
    {
      Header: "Name",
      accessor: "fullName",
      minWidth: 190,
      maxWidth: 190,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    },
  
    {
      Header: "Contact Info",
      id: "contactInfo",
      minWidth: 190,
      maxWidth: 190,
      Cell: ({ row }) => {
        const { email, mobile } = row.original;

        return (
          <div className="d-flex flex-column text-wrapper">
            <span className="mb-1 d-flex align-items-start">
              <i className="fas fa-envelope mr-2 text-muted flex-shrink-0 contact-info-icon" />
              <span className="mt--1">{email || "-"}</span>
            </span>

            <span className="d-flex align-items-start">
              <i className="fas fa-phone mr-2 text-muted flex-shrink-0 contact-info-icon" />
              <span className="mt--1">{mobile || "-"}</span>
            </span>
          </div>
        );
      },
    },
    {
      Header: "Experience",
      accessor: "totalExperience",
      minWidth: 130,
      maxWidth: 130,
      Cell: ({ value }) => <span className="text-wrapper">{value ?? "-"}</span>,
    },
    {
      Header: "Skills",
      accessor: "skills",
      minWidth: 200,
      maxWidth: 200,
      Cell: ({ row }) => {
        const skills = (row.original.skills || []).filter((skill) =>
          skill?.trim(),
        );

        const { ID } = row.original;

        if (!skills.length) {
          return "—";
        }

        const visibleSkills = skills.slice(0, 2);
        const remainingCount = skills.length - 2;
        const skillPopoverId = `skill-${ID}`;

        return (
          <div className="text-wrapper">
            {visibleSkills.map((skill, index) => (
              <div className="trainer-name" key={index}>
                <div className="trainer-row">
                  <span className="trainer-bullet">•</span>
                  <span className="trainer-text">{skill}</span>
                </div>

                {index === 1 && remainingCount > 0 && (
                  <>
                    <div className="trainer-count">
                      <Badge
                        id={skillPopoverId}
                        color="primary"
                        pill
                        className="cursor-pointer"
                      >
                        + {remainingCount}
                      </Badge>
                    </div>

                    <UncontrolledPopover
                      target={skillPopoverId}
                      trigger="hover"
                      placement="top"
                    >
                      <PopoverBody>
                        <div className="trainer-popover-list">
                          {skills.map((skill, skillIndex) => (
                            <div key={skillIndex}>• {skill}</div>
                          ))}
                        </div>
                      </PopoverBody>
                    </UncontrolledPopover>
                  </>
                )}
              </div>
            ))}
          </div>
        );
      },
    },
    {
      Header: "Gender",
      accessor: "gender",
      minWidth: 95,
      maxWidth: 95,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    },
    {
      Header: "Status",
      accessor: "status",
      minWidth: 80,
      maxWidth: 80,
      centerAlign: true,
      Cell: ({ value }) => (
        <Badge color={value === "ACTIVE" ? "success" : "warning"} pill>
          {value}
        </Badge>
      ),
    },
      {
      Header: "Updated By",
      accessor: "createdBy",
      centerAlign:true,
      minWidth: 120,
      maxWidth: 140,
      Cell: ({ value }) => <div className="text-wrapper">{value || "—"}</div>,
    },
    {
      Header: "Registered On",
      accessor: "createdOn",
      minWidth: 190,
      maxWidth: 190,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    },
    {
      Header: "Actions",
      disableSortBy: true,
      minWidth: 100,
      maxWidth: 100,
      Cell: ({ row }) => {
        const encodedId = urlEncodedId(row.original.ID);
        const isActive = row.original.status === "ACTIVE";

        return (
          <div className="d-flex">
            <Button
              className="btn-link d-flex align-items-center p-0"
              id={`edit${encodedId}`}
              size="sm"
              onClick={(e) => {
                e.stopPropagation();

                const encodedId = urlEncodedId(row.original.ID);

                navigate(
                  role === "SUPER_ADMIN"
                    ? `/super-admin/instructor/edit/${encodedId}`
                    : `/admin/instructor/edit/${encodedId}`,
                  {
                    state: { mode: "edit" },
                  },
                );
              }}
            >
              <i className="fas fa-edit mr-1 table-action-info" />
              {/* Edit */}
            </Button>
            <UncontrolledTooltip target={`edit${encodedId}`}>
              Edit
            </UncontrolledTooltip>

            <span className="mr-1 text-muted">|</span>
            <Button
              color="link"
              size="lg"
              className="d-flex align-items-center p-0 ml-1"
              id={`status${encodedId}`}
              onClick={(e) => {
                e.stopPropagation();
                toggleStatus(row.original);
              }}
            >
              <i
                className={`fas status-toggle-icon ${
                  isActive
                    ? "fa-toggle-on text-success"
                    : "fa-toggle-off text-danger"
                }`}
              />
            </Button>
            <UncontrolledTooltip delay={0} target={`status${encodedId}`}>
              {isActive ? "Inactivate" : "Activate"}
            </UncontrolledTooltip>

            {/* <Button
              className="btn-link d-flex align-items-center p-0 table-action-delete"
              id={`delete${id}`}
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <i className="fas fa-trash mx-1 table-action-delete" />
            </Button>
            <UncontrolledTooltip target={`delete${id}`}>
              Delete
            </UncontrolledTooltip> */}
          </div>
        );
      },
    },
  ];

  const toggleStatus = (data) => {
    const isActive = data.status === "ACTIVE";
    const newStatus = isActive ? "INACTIVE" : "ACTIVE";
    showAlert({
      title: `Are you sure you want to ${
        isActive ? "inactivate" : "activate"
      } this instructor?`,
      message: `Instructor "${data.fullName}" will be marked as ${newStatus.toLowerCase()}.`,
      type: "warning",
      confirmStyle: "deep-blue",
      cancelStyle: "secondary",
      confirmText: "Yes",
      cancelText: "Cancel",
      onConfirm: async (closeAlert) => {
        closeAlert();
        try {
          setIsUpdatingStatus(true);
          const res = await editInstructor(data.ID, {
            status: newStatus,
          });
          if (res.code === 200) {
            notify(
              "success",
              res?.message || `Status updated to ${newStatus.toLowerCase()}`,
            );
            getInstructorList(dispatch, pageNum, searchState.filterParams);
          } else {
            const message = Array.isArray(res?.message)
              ? res.message
                  .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
                  .join(", ")
              : res?.message || "Failed to update status!";

            notify("danger", message);
          }
        } catch (error) {
          notify("danger", error?.message || "Error updating status");
        } finally {
          setIsUpdatingStatus(false);
        }
      },
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleSearch = () => {
    const newfilterParams = buildFilterParams();

    if (
      JSON.stringify(searchState.filterParams) !==
      JSON.stringify(newfilterParams)
    ) {
      setInstructorListState((prev) => ({
        ...prev,
        isLoading: true,
        showLoaderOnClick: true,
      }));
      setSearchState((prev) => ({
        ...prev,
        filterParams: newfilterParams,
      }));

      if (pageNum !== 1) setPageNum(1);
    }
  };

  const handleClear = () => {
    setInstructorListState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    setSearchState({
      searchName: "",
      searchEmail: "",
      searchMobile: "",
      searchStatus: null,
      filterParams: [],
    });
    if (pageNum !== 1) setPageNum(1);
  };
  const handleMobileChange = (e) => {
    const value = e.target.value;
    if (!REGEX.ONLY_DIGITS_REGEX.test(value)) {
      return;
    }
    setSearchState((prev) => ({
      ...prev,
      searchMobile: value,
    }));
  };

  // Custom Option Renderer
  const Option = (props) => {
    return (
      <components.Option {...props}>
        <Badge
          color={props.data.value === "ACTIVE" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.Option>
    );
  };

  // Custom SingleValue Renderer (for the selected value)
  const SingleValue = (props) => {
    return (
      <components.SingleValue {...props}>
        <Badge
          color={props.data.value === "ACTIVE" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.SingleValue>
    );
  };

  return (
    <>
      <AdminHeader />
      {isUpdatingStatus && (
        <div className="logout-overlay">
          <div className="logout-card">
            <i className="fa fa-spinner fa-spin fa-2x text-deep-blue mb-4" />
            <div className="font-weight-bold text-dark">
              Updating status... Please wait.
            </div>
          </div>
        </div>
      )}
      <Container className="mt--6" fluid>
        <Row>
          <Col>
            <Card>
              {/* head */}
              <CardHeader className="d-flex justify-content-between align-items-center">
                <CardTitle tag="h3" className="mb-0 text-default">
                  Instructors
                </CardTitle>
                <Button
                  className="btn-deep-blue"
                  onClick={() =>
                    navigate(
                      role === "SUPER_ADMIN"
                        ? "/super-admin/instructor/add-new"
                        : "/admin/instructor/add-new",
                      {
                        state: { mode: "add" },
                      },
                    )
                  }
                >
                  <i className="fa fa-plus mr-1" />
                  Instructor
                </Button>
              </CardHeader>

              {/* body */}
              <CardBody>
                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap flex-column flex-md-row">
                  {/* Left side - Filter Inputs + Search */}
                  <div className="d-flex flex-wrap align-items-center filter-group">
                    <Input
                      type="text"
                      placeholder="Search by name"
                      className="mr-2 mb-2 filter-input-large"
                      value={searchState.searchName}
                      onChange={(e) =>
                        setSearchState((prev) => ({
                          ...prev,
                          searchName: e.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    <Input
                      type="text"
                      placeholder="Search by email"
                      className="mr-2 mb-2 filter-input-large"
                      value={searchState.searchEmail}
                      onChange={(e) =>
                        setSearchState((prev) => ({
                          ...prev,
                          searchEmail: e.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    <Input
                      type="text"
                      placeholder="Search by mobile no."
                      className="mr-2 mb-2 filter-input-large"
                      value={searchState.searchMobile}
                      onChange={handleMobileChange}
                      onKeyDown={handleKeyDown}
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={STATUS_OPTIONS}
                      placeholder="Select status"
                      value={searchState.searchStatus}
                      onChange={(selected) =>
                        setSearchState((prev) => ({
                          ...prev,
                          searchStatus: selected,
                        }))
                      }
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      components={{ Option, SingleValue }}
                      onKeyDown={handleKeyDown}
                      // isClearable
                    />

                    <Button
                      color="default"
                      className={`d-flex align-items-center mb-2 filter-btn btn-deep-blue ${instructorListState.showLoaderOnClick ? "search-btn" : ""}`}
                      onClick={handleSearch}
                    >
                      {instructorListState.showLoaderOnClick ? (
                        <i className="fa fa-spinner fa-spin loader" />
                      ) : (
                        <>
                          <i className="fa fa-search mr-1 " />
                          Search
                        </>
                      )}
                    </Button>

                    {searchState.filterParams.length > 0 && (
                      <Button
                        color="default"
                        className="mb-2 filter-btn btn-simple btn-deep-blue"
                        onClick={handleClear}
                      >
                        <i className="fa fa-times mr-1" /> Clear
                      </Button>
                    )}
                  </div>
                </div>
                <div className="mt-2 mb-3 mx-1">
                  {instructorListData?.data?.totalRecords ? (
                    <>
                      {searchState.filterParams?.length > 0 ? (
                        <span className="total-records">
                          Showing {instructorListData?.data?.filteredRecords} of{" "}
                          {instructorListData?.data?.totalRecords} Records
                        </span>
                      ) : (
                        <span className="total-records">
                          Total Records :{" "}
                          {instructorListData?.data?.totalRecords}
                        </span>
                      )}
                    </>
                  ) : null}
                </div>

                {instructorListState.isLoading ? (
                  <div className="loaderContainer">
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </div>
                ) : instructorListState.error ? (
                  <>
                    <div className="loaderContainer error-style">
                      <div className="error-icon">
                        <i class="fa-solid fa-circle-exclamation"></i>
                      </div>
                      <span>{instructorListState.error}</span>
                    </div>
                    <div className="loaderContainer">
                      {instructorListState.error}
                    </div>
                  </>
                ) : instructorListState.noRecords ? (
                  <div className="loaderContainer empty-style">
                    <div className="empty-icon">
                      <i class="fa-solid fa-circle-info"></i>
                    </div>
                    <span>No instructors found.</span>
                  </div>
                ) : (
                  <CommonTable
                    columns={columns}
                    data={formattedInstructorList || []}
                    defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
                    activePage={pageNum}
                    setPageNum={setPageNum}
                    totalPages={Math.ceil(
                      instructorListData?.data?.filteredRecords /
                        instructorListData?.data?.noOfRecordsPerPage ||
                        DEFAULT_RECORDS_PER_PAGE,
                    )}
                    showPaginationBottom={true}
                  />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default Instructors;
